import {
  DeleteTeamInviteDto,
  DeleteTeamMemberDto,
  fetchTeamInviteDelete,
  fetchTeamMemberDelete,
  TeamInvites,
  TeamMembers,
  teamInvitesQueryOptions,
  teamMembersQueryOptions,
} from '@blesk-frontend/api';
import {useBleskAuth} from '@blesk-frontend/auth';
import {
  Button,
  ModalConfirm,
  ModalProps,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  useModal,
} from '@blesk-frontend/ui';
import {useQueryClient} from '@tanstack/react-query';
import {toast} from 'sonner';

import {copyToClipboard, shortDateTimeFormat} from '@/libs';

interface TeamMembersTableProps {
  teamId: string;
  isOwner: boolean;
  teamMembers?: TeamMembers[];
  teamInvites?: TeamInvites[];
}

export const TeamMembersTable = ({
  teamId,
  isOwner,
  teamMembers,
  teamInvites,
}: TeamMembersTableProps) => {
  const {
    query: {data: user},
  } = useBleskAuth();
  const queryClient = useQueryClient();

  const onDeleteTeamMember = async (dto: DeleteTeamMemberDto) => {
    await fetchTeamMemberDelete(dto);
    queryClient.invalidateQueries({queryKey: teamMembersQueryOptions(teamId).queryKey});
    queryClient.invalidateQueries({queryKey: teamInvitesQueryOptions(teamId).queryKey});
  };

  const onDeleteTeamInvite = async (dto: DeleteTeamInviteDto) => {
    await fetchTeamInviteDelete(dto);
    queryClient.invalidateQueries({queryKey: teamMembersQueryOptions(teamId).queryKey});
    queryClient.invalidateQueries({queryKey: teamInvitesQueryOptions(teamId).queryKey});
  };

  const openDeleteTeamMemberModal = useModal<ModalProps & DeleteTeamMemberDto>((props) => (
    <ModalConfirm
      {...props}
      title="Удалить пользователя"
      message="Подтвердите, что вы точно хотите удалить пользователя из вашей команды."
      labels={{cancel: 'Отменить', confirm: 'Удалить'}}
      onConfirm={() => onDeleteTeamMember({teamId: props.teamId, userId: props.userId})}
    />
  ));

  const openDeleteTeamInviteModal = useModal<ModalProps & DeleteTeamInviteDto>((props) => (
    <ModalConfirm
      {...props}
      title="Удалить приглашение"
      message="Подтвердите, что вы точно хотите удалить приглашение пользователя в вашу команду."
      labels={{cancel: 'Отменить', confirm: 'Удалить'}}
      onConfirm={() => onDeleteTeamInvite({code: props.code, email: props.email})}
    />
  ));

  return (
    <Table>
      <TableHeader>
        <TableRow className="text-text-disabled">
          <TableHead className="w-1/6">Email</TableHead>
          <TableHead className="w-1/6">Имя пользователя</TableHead>
          <TableHead className="w-1/6">Статус</TableHead>
          <TableHead className="w-1/6">Добавлен</TableHead>
          <TableHead className="w-1/6">Последний вход</TableHead>
          {isOwner && <TableHead className="w-1/12" />}
        </TableRow>
      </TableHeader>
      <TableBody>
        {!((teamMembers?.length || 1) < 2 && !teamInvites?.length) &&
          teamMembers?.map((member) => (
            <TableRow key={`team-member-${member.user.email}`}>
              <TableCell>{member.user.email}</TableCell>
              <TableCell>{member.user.fullName || '—'}</TableCell>
              <TableCell>Активен</TableCell>
              <TableCell>
                {shortDateTimeFormat.format(new Date(member.createdAt as string))}
              </TableCell>
              <TableCell>
                {member.user.events?.at(0)?.payload.timestamp
                  ? shortDateTimeFormat.format(
                      new Date(member.user.events?.at(0)?.payload?.timestamp as unknown as string),
                    )
                  : '—'}
              </TableCell>
              {isOwner && member.userId !== user?.id && (
                <TableCell>
                  {member.code ? (
                    <Button
                      onClick={() =>
                        openDeleteTeamInviteModal({
                          code: member.code as string,
                          email: member.user.email,
                        })
                      }
                      variant="link"
                      size="icon"
                      className="h-fit text-error-primary hover:text-error-primary-hover"
                    >
                      Удалить
                    </Button>
                  ) : (
                    <Button
                      onClick={() =>
                        openDeleteTeamMemberModal({teamId, userId: member.userId as string})
                      }
                      variant="link"
                      size="icon"
                      className="h-fit text-error-primary hover:text-error-primary-hover"
                    >
                      Удалить
                    </Button>
                  )}
                </TableCell>
              )}
            </TableRow>
          ))}

        {teamInvites?.map((invite) => (
          <TableRow key={`team-invite-${invite.code}`}>
            <TableCell>{invite.email}</TableCell>
            <TableCell>—</TableCell>
            <TableCell>
              <Tooltip message="Скопировать ссылку-приглашение">
                <Button
                  onClick={async () => {
                    await copyToClipboard(`${location.origin}/auth/invite?code=${invite.code}`);
                    toast.success('Приглашение скопировано');
                  }}
                  variant="link"
                  size="icon"
                  className="h-fit w-fit text-text-primary hover:text-text-primary"
                >
                  Приглашен
                </Button>
              </Tooltip>
            </TableCell>
            <TableCell>
              {shortDateTimeFormat.format(new Date(invite.createdAt as string))}
            </TableCell>
            <TableCell>Неизвестно</TableCell>
            <TableCell>
              <Button
                onClick={() =>
                  openDeleteTeamInviteModal({
                    code: invite.code as string,
                    email: invite.email,
                  })
                }
                variant="link"
                size="icon"
                className="h-fit text-error-primary hover:text-error-primary-hover"
              >
                Удалить
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>

      {(teamMembers?.length || 1) < 2 && !teamInvites?.length && (
        <TableFooter>
          <TableRow className="h-25 text-center font-normal text-paragraph-sm text-text-secondary">
            <TableCell colSpan={6}>В команде пока только вы</TableCell>
          </TableRow>
        </TableFooter>
      )}
    </Table>
  );
};
